import React, { useState } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';

/**
 * KeyValuePairInput - For mapping relationships (e.g., restricted phrase → safe alternative)
 * @param {string} label - Field label
 * @param {Object} pairs - Current key-value pairs object
 * @param {Function} onChange - Callback when pairs change
 * @param {string} keyLabel - Label for key field
 * @param {string} valueLabel - Label for value field
 * @param {string} helper - Helper text with example
 * @param {boolean} required - Is field required
 */
const KeyValuePairInput = ({
  label,
  pairs = {},
  onChange,
  keyLabel = 'Key',
  valueLabel = 'Value',
  helper = '',
  required = false,
}) => {
  const [keyInput, setKeyInput] = useState('');
  const [valueInput, setValueInput] = useState('');

  const handleAddPair = () => {
    if (!keyInput.trim() || !valueInput.trim()) return;
    
    const newPairs = { ...pairs, [keyInput.trim()]: valueInput.trim() };
    onChange(newPairs);
    setKeyInput('');
    setValueInput('');
  };

  const handleRemovePair = (key) => {
    const newPairs = { ...pairs };
    delete newPairs[key];
    onChange(newPairs);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPair();
    }
  };

  const pairEntries = Object.entries(pairs);

  return (
    <div style={{ marginBottom: EMZI_SPACING.md }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: EMZI_SPACING.xs,
        color: EMZI_COLORS.textPrimary,
      }}>
        {label}
        {required && <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>}
        <span style={{ 
          fontSize: '12px', 
          fontWeight: '400',
          color: EMZI_COLORS.textSecondary,
          marginLeft: EMZI_SPACING.xs 
        }}>
          ({pairEntries.length} pairs)
        </span>
      </label>

      {helper && (
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 {helper}
        </div>
      )}

      {/* Input fields */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr auto',
        gap: EMZI_SPACING.xs,
        marginBottom: EMZI_SPACING.xs,
      }}>
        <input
          type="text"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={keyLabel}
          style={{
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
        <input
          type="text"
          value={valueInput}
          onChange={(e) => setValueInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={valueLabel}
          style={{
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={handleAddPair}
          disabled={!keyInput.trim() || !valueInput.trim()}
          style={{
            padding: `${EMZI_SPACING.sm} ${EMZI_SPACING.md}`,
            background: keyInput.trim() && valueInput.trim() ? EMZI_COLORS.primary : EMZI_COLORS.surface,
            color: keyInput.trim() && valueInput.trim() ? '#FFFFFF' : EMZI_COLORS.textMuted,
            border: 'none',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '500',
            cursor: keyInput.trim() && valueInput.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          Add
        </button>
      </div>

      {/* Pairs list */}
      {pairEntries.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: EMZI_SPACING.xs,
          maxHeight: '200px',
          overflowY: 'auto',
        }}>
          {pairEntries.map(([key, value]) => (
            <div
              key={key}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto',
                alignItems: 'center',
                gap: EMZI_SPACING.sm,
                padding: EMZI_SPACING.sm,
                background: EMZI_COLORS.surface,
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '14px',
              }}
            >
              <span style={{ 
                color: EMZI_COLORS.textPrimary,
                fontWeight: '500',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {key}
              </span>
              <span style={{ 
                color: EMZI_COLORS.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                → {value}
              </span>
              <button
                type="button"
                onClick={() => handleRemovePair(key)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: EMZI_COLORS.error,
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KeyValuePairInput;
